import maplibregl from "maplibre-gl";
import {
  convertIconSpecToData,
  iconToSvgElement,
} from "../../../components/icons/iconToSvg";
import { IconMap } from "../components/icons/IconMap";

const TYPE_ORDER = {
  transportation: 1,
  accommodation: 2,
  activity: 3,
  tracks: 4,
};

function getTypeOrder(type) {
  const key = (type || "").toLowerCase();
  return TYPE_ORDER[key] ?? 99;
}

/**
 * Create an advanced popup element for a cluster.
 *
 * @param {object} options
 * @param {object} options.cluster - clusterGroup
 * @param {function} options.onSelectMarkerGroup - called with markerGroup when user clicks
 * @returns {maplibregl.Popup}
 */
export function createClusterGroupPopup({ cluster, onSelectMarkerGroup }) {
  const popupEl = document.createElement("div");
  popupEl.className = "cluster-popup";

  const headerEl = document.createElement("div");
  headerEl.className = "cluster-popup__header";
  headerEl.textContent = `Select from ${cluster.members.length} locations.`;
  popupEl.appendChild(headerEl);

  const listEl = document.createElement("ul");
  listEl.className = "cluster-popup__list";

  // Sort members by segment.type using desired order, and for tracks
  // also sort by created timestamp DESC (newest → oldest)
  const sortedMembers = [...cluster.members].sort((a, b) => {
    const segA = a.anchorSegment;
    const segB = b.anchorSegment;

    // 1) Primary sort: by type order
    const oA = getTypeOrder(segA?.type);
    const oB = getTypeOrder(segB?.type);
    if (oA !== oB) return oA - oB;

    // ---------------------------------------------------------
    // 2) Secondary sort ONLY when the type is "tracks":
    //    Sort by created timestamp DESC (newest first)
    // ---------------------------------------------------------
    const type = (segA?.type || "").toLowerCase();
    if (type === "tracks") {
      // Convert undefined → 0 so missing timestamps fall to end
      const tA = Number(segA?.created || 0);
      const tB = Number(segB?.created || 0);

      // DESCENDING → larger timestamp first
      if (tA !== tB) return tA - tB;
    }

    // ---------------------------------------------------------
    // 3) Normal fallback: sort by trip then id
    // ---------------------------------------------------------

    const tripA = segA?.trip || "";
    const tripB = segB?.trip || "";
    if (tripA !== tripB) return tripA.localeCompare(tripB);

    return String(segA?.id || "").localeCompare(String(segB?.id || ""));
  });

  // previous
  //   // Sort members by segment.type using desired order
  //   const sortedMembers = [...cluster.members].sort((a, b) => {
  //     const segA = a.anchorSegment;
  //     const segB = b.anchorSegment;
  //     const oA = getTypeOrder(segA?.type);
  //     const oB = getTypeOrder(segB?.type);
  //     if (oA !== oB) return oA - oB;

  //     // tie-breaker: by trip then id, if present
  //     const tA = segA?.trip || "";
  //     const tB = segB?.trip || "";
  //     if (tA !== tB) return tA.localeCompare(tB);

  //     return String(segA?.id || "").localeCompare(String(segB?.id || ""));
  //   });

  for (const mg of sortedMembers) {
    const seg = mg.anchorSegment;

    const itemEl = document.createElement("li");
    itemEl.className = "cluster-popup__item";

    // Marker element
    const mkrEl = document.createElement("div");
    const routeLabelEl = document.createElement("div");
    mkrEl.className = `map-marker ${seg.type}`;
    let iconEl,
      svgData = "";

    let routeLabel = "";

    switch (seg.type) {
      case "transportation":
        svgData = convertIconSpecToData({
          iconSpec: IconMap[seg.mode],
          size: 22,
        });
        iconEl = iconToSvgElement(svgData);
        routeLabel = `${seg.originCode} → ${seg.destinationCode}`;
        break;
      case "accommodation":
        svgData = convertIconSpecToData({
          iconSpec: IconMap[seg.spaceType],
          size: 22,
        });
        iconEl = iconToSvgElement(svgData);
        routeLabel = `${seg.providerName}`;
        break;
      case "activity":
        svgData = convertIconSpecToData({
          iconSpec: IconMap[seg.activityType],
          size: 22,
        });
        iconEl = iconToSvgElement(svgData);
        routeLabel = `${seg.activityName}`;
        break;
      case "tracks":
        svgData = convertIconSpecToData({
          iconSpec: IconMap[seg.subType],
          size: 22,
        });
        iconEl = iconToSvgElement(svgData);
        routeLabel = `${seg.title}`;
        break;
      default:
        mkrEl.classList.add("map-marker--dot");
        routeLabel = `${seg.type}`;
        break;
    }

    mkrEl.append(iconEl);
    routeLabelEl.innerText = routeLabel;

    const line1 = document.createElement("div");
    line1.className = "cluster-popup__title";
    line1.append(mkrEl);
    line1.append(routeLabelEl);

    // const typeLabel = "";
    // //   (seg?.type || "").charAt(0).toUpperCase() + (seg?.type || "").slice(1);

    // // example title: "Transportation – DEN → ATL"
    // // let routeLabel = "";
    // // if (seg?.originCode && seg?.destinationCode) {
    // //   routeLabel = `${seg.originCode} → ${seg.destinationCode}`;
    // // } else if (seg?.name) {
    // //   routeLabel = seg.name;
    // // } else if (seg?.trip) {
    // //   routeLabel = seg.trip;
    // // }

    // line1.textContent = `${typeLabel}${routeLabel ? " – " + routeLabel : ""}`;

    // const line2 = document.createElement("div");
    // line2.className = "cluster-popup__meta";
    // // You can customize this however you want:
    // // dates, times, provider, etc.
    // if (seg?.departureDate || seg?.arrivalDate) {
    //   const dep = seg.departureDate || "";
    //   const arr = seg.arrivalDate || "";
    //   line2.textContent = [dep, arr].filter(Boolean).join(" → ");
    // }

    itemEl.appendChild(line1);
    // if (line2.textContent) itemEl.appendChild(line2);

    itemEl.addEventListener("click", () => {
      onSelectMarkerGroup?.(mg);
    });

    listEl.appendChild(itemEl);
  }

  // Make the list scrollable if tall
  popupEl.appendChild(listEl);

  const popup = new maplibregl.Popup({ closeButton: true });
  popup.setDOMContent(popupEl);

  return popup;
}
